import { alias, equal, not } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import Enum from '@square/dashboard-utilities/utils/enum';
import InventoryAdjustment from '@square/dashboard-items-data/models/squareup/inventory/v3/inventory-adjustment';
import InventoryCount from '@square/dashboard-items-data/models/squareup/inventory/v3/inventory-count';
import InventoryQuantity from '@square/dashboard-items-data/models/squareup/inventory/v3/inventory-quantity';
import { Area, Length, Volume, Weight, Time, Generic } from '@square/dashboard-items-data/models/connect/measurement-unit/unit';
import CatalogComponent from '@square/dashboard-items-data/models/squareup/inventory/v3/catalog-component';
import QuantityRatio from '@square/dashboard-items-data/models/squareup/inventory/v3/quantity-ratio';
import ProductIdentifier from '@square/dashboard-items-data/models/squareup/inventory/v3/product-identifier';
import { VendorState } from '@square/dashboard-items-data/models/squareup/inventory/v3/vendor';
const CustomUnit = ProtoBaseModel.extend({
  name: attr(String),
  abbreviation: attr(String)
});
const MeasurementUnit = ProtoBaseModel.extend({
  customUnit: attr(CustomUnit),
  areaUnit: attr(Area),
  lengthUnit: attr(Length),
  volumeUnit: attr(Volume),
  weightUnit: attr(Weight),
  timeUnit: attr(Time),
  genericUnit: attr(Generic)
});

// This model is intended for usages within Inventory Event and its related models
const CatalogMeasurementUnit = ProtoBaseModel.extend({
  measurementUnit: attr(MeasurementUnit),
  precision: attr(Number)
});
const ComponentInformation = ProtoBaseModel.extend({
  productIdentifier: attr(ProductIdentifier),
  measurementUnit: attr(CatalogMeasurementUnit),
  component: attr(CatalogComponent),
  compositionQuantity: attr(QuantityRatio),
  catalogMeasurementUnit: alias('measurementUnit')
});
export const CompositionType = new Enum(['BUNDLE', 'STOCK_CONVERSION']);
export const ComposedObjectType = new Enum(['BUNDLE', 'STOCK_CONVERSION', 'RECIPE']);
const RootAdjustmentInformation = ProtoBaseModel.extend({
  productIdentifier: attr(ProductIdentifier),
  itemName: attr(String),
  variationName: attr(String),
  measurementUnit: attr(CatalogMeasurementUnit),
  measurementUnitToken: attr(String),
  compositionType: attr(CompositionType),
  quantityDecimal: attr(String),
  isDeleted: attr(Boolean),
  composedObjectType: attr(ComposedObjectType)
});
export const InventoryEventVendor = ProtoBaseModel.extend({
  token: attr(String),
  name: attr(String),
  state: attr(VendorState),
  isActive: equal('state', VendorState.VENDOR_ACTIVE),
  isDisabled: not('isActive')
}).reopenClass({
  fromVendorModel(vendorModel) {
    return isNone(vendorModel) ? undefined : this.create({
      token: vendorModel.token,
      name: vendorModel.name,
      state: vendorModel.state
    });
  }
});
export const InventoryEventType = new Enum(['SALE', 'RESTOCK', 'RECEIVED', 'DAMAGED', 'LOSS', 'RECOUNT', 'TRANSFER', 'CANCELED_SALE', 'IN_TRANSIT']);
export default ProtoBaseModel.extend({
  // Maps to squareup.inventory.v3.InventoryEventType proto.
  eventType: attr(String),
  // adjustment and count are in ONE_OF relationship to each other.
  // Only one of them will be set and the other will be null.
  adjustment: attr(InventoryAdjustment),
  stockCount: attr(InventoryCount),
  inferredAdjustment: attr(InventoryAdjustment),
  itemName: attr(String),
  variationName: attr(String),
  variationSku: attr(String),
  variationUpc: attr(String),
  defaultCost: attr(MoneyProto),
  /**
   * Contains the item variation's default vendor, if it has one.
   * Only populated if "include_default_vendor" is set to true on the inventory events query.
   * @type {Vendor}
   */
  defaultVendor: attr(InventoryEventVendor),
  objectDeleted: attr(Boolean),
  connectOauthClientName: attr(String),
  maxEditableQuantity: attr(InventoryQuantity),
  vendors: repeated(InventoryEventVendor),
  linkedRestockOriginalSaleUnitToken: attr(String),
  transferSourceUnitToken: attr(String),
  measurementUnit: attr(CatalogMeasurementUnit),
  componentInformation: attr(ComponentInformation),
  rootAdjustmentInformation: attr(RootAdjustmentInformation)
});